import { useRef, useState, useEffect } from "react";
import AnimatedTitle from "./AnimatedTitle";
import ContactButton from "./ContactButton";
import ContactFormPopup from "./ContactFormPopup";

const ImageClipBox = ({ src, clipClass, alt = "Decorative image" }) => (
  <div className={clipClass}>
    <img 
      src={src} 
      alt={alt}
      loading="lazy"
      decoding="async"
      className="size-full object-cover"
    />
  </div>
);


const Contact = () => {
  const sectionRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(true);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [backgroundVideoLoaded, setBackgroundVideoLoaded] = useState(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
          setImagesLoaded(true);
        }
      },
      { 
        threshold: [0, 0.1],
        rootMargin: '100px 0px'
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const getImageSrc = (imageName) => {
    const CDN_BASE = 'https://storage.googleapis.com/tll-videos-for-web';
    return `${CDN_BASE}/${imageName}`;
  };

  const handleContactClick = () => {
    setIsContactFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsContactFormOpen(false);
  };

  const handleBackgroundVideoLoad = () => {
    setBackgroundVideoLoaded(true);
    console.log('Background video loaded successfully');
  };

  const handleBackgroundVideoError = (e) => {
    console.error('Background video failed to load:', e);
    setBackgroundVideoLoaded(false);
  };

  return (
    <>
      <div ref={sectionRef} id="contact" className="my-20 min-h-96 w-screen px-4 md:px-10">
        <div className="relative overflow-hidden rounded-lg text-blue-50">
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            <video
              src="https://storage.googleapis.com/tll-videos-for-web/3DEnvFlythrough.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="size-full object-cover"
              style={{ filter: 'brightness(0.6)' }}
              onLoadedData={handleBackgroundVideoLoad}
              onError={handleBackgroundVideoError}
              onCanPlay={() => console.log('Background video can play')}
            />
            {/* Fallback background if video fails */}
            {!backgroundVideoLoaded && (
              <div className="absolute inset-0 bg-gray-900"></div>
            )}
            {/* Lighter overlay for better video visibility */}
            <div className="absolute inset-0 bg-black/40"></div>
          </div>

          {/* Content overlay */}
          <div className="relative z-10 py-16 md:py-24">
            <div className="absolute -left-20 top-0 hidden h-full w-72 overflow-hidden sm:block lg:left-20 lg:w-96">
              {imagesLoaded && (
                <>
                  <ImageClipBox
                    src={getImageSrc("NeuralNetwork1.webp")}
                    clipClass="contact-clip-path-1"
                  />
                  <ImageClipBox
                    src={getImageSrc("3DCode.webp")}
                    clipClass="contact-clip-path-2 lg:translate-y-40 translate-y-60"
                  />
                </>
              )}
            </div>

            {/* <div className="absolute -top-40 left-20 w-60 sm:top-1/2 md:left-auto md:right-10 lg:top-20 lg:w-80">
              <VideoClipBox
                src="https://storage.googleapis.com/tll-videos-for-web/3DEnvFlythrough_Vert.mp4"
                clipClass="sword-man-clip-path md:scale-125"
                shouldLoad={shouldLoadVideo}
              />
            </div> */}

            <div className="flex flex-col items-center px-4 text-center">
              <p className="mb-6 font-general text-[8px] uppercase md:mb-10 md:text-[10px]">
                {/* Content here if needed */}
              </p>

              <AnimatedTitle
                title="REA<b>D</b>Y TO REV<b>O</b>LUTIONIZE Y<b>O</b>UR SYS<b>T</b>EMS?"
                className="special-font mx-auto w-full max-w-6xl break-words px-4 font-zentry text-[1.5rem] font-black leading-tight sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-[6.2rem]"
              />

              <div className="mt-6 md:mt-8">
                <ContactButton onClick={handleContactClick} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Popup */}
      <ContactFormPopup 
        isOpen={isContactFormOpen} 
        onClose={handleCloseForm} 
      />
    </>
  );
};

export default Contact;